import { AddToPlaylistButton } from '@/components/playlist/add-to-playlist-button';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useAudio } from '@rhg/hooks/use-audio';
import { useAudioTiming } from '@rhg/hooks/use-audio-timing';
import { useGuideQueue } from '@rhg/hooks/use-guide-queue';
import { Box, Slide, SxProps, Theme, Typography } from '@rhg/libs/material';
import { styleMaxLines, sxAppend } from '@rhg/libs/material/sx';
import { LoopCapability } from '@rhg/providers/guide-queue/types';
import { NORMAL_PLAYBACK_RATE } from '@rhg/utils/media';
import { MediaSubtitles } from '../../subtitles';
import { AudioTimeline } from '../timeline';
import {
    AudioControlsProps,
    AudioPlayerControls,
    AudioPlayerVariant,
    AudioTimelineVariant,
} from '../types';
import { CcButton } from './cc-button';
import { LoopButton } from './loop-button';
import { MuteButton } from './mute-button';
import { NextButton } from './next-button';
import { PlayPauseButton } from './play-pause-button';
import { PlaybackSpeedButton } from './playback-speed-button';
import { PreviousButton } from './previous-button';
import { ShuffleButton } from './shuffle-button';
import {
    styleAudioControlsContainer,
    styleBottomActionsRow,
    styleCenteredSubtitlesWrapper,
    styleControlsButtons,
    styleControlsButtonsCollapsed,
    styleControlsButtonsNoTimeline,
    styleControlsButtonsTimeline,
    styleControlsCenter,
    styleControlsMain,
    styleCustomActionContainer,
    styleDrawerTitleIcon,
    styleFloatingContainer,
    styleFloatingSubtitles,
    styleMetadata,
    styleMetadataContainer,
    styleNonMobileElement,
    styleSubtitles,
    styleTitleContainer,
    styleTitleSubtitle,
} from './styles';

// Isolates the `currentCue` subscription so the ~4Hz timing context re-renders
// only this leaf, not the whole AudioControls transport row. EVE-12668.
function AudioControlsSubtitles({
    fadeIn,
    isMuted,
    sx,
}: {
    fadeIn?: boolean;
    isMuted: boolean;
    sx?: SxProps<Theme>;
}) {
    const { currentCue } = useAudioTiming();
    return (
        <MediaSubtitles
            fadeIn={fadeIn}
            cue={currentCue}
            isMuted={isMuted}
            sx={sx}
        />
    );
}

export function AudioControls({
    areControlsHidden,
    sx,
    controls = AudioPlayerControls.Regular,
    supportsPlaybackSpeed = true,
    variant = AudioPlayerVariant.Collapsed,
    isFloating = false,
    title,
    subtitle,
    image,
    customAction,
    supportsAddToPlaylist = false,
    onAddToPlaylist,
    controlsSx,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
    drawerTitleIcon,
    onPlaybackMenuOpenChange,
}: AudioControlsProps) {
    const isMobile = useIsMobile('lg');

    const isSoundScape = controls === AudioPlayerControls.Soundscape;
    const isPlaylist =
        controls === AudioPlayerControls.Playlist ||
        controls === AudioPlayerControls.MusicAlbum;
    const isExpanded = variant === AudioPlayerVariant.Expanded;
    const isCollapsed = variant === AudioPlayerVariant.Collapsed;

    const {
        controls: {
            isMuted,
            isShowingSubtitles,
            playbackRate,
            setPlaybackRate,
        },
    } = useAudio();

    // Loop and shuffle controls render whenever the queue reports the
    // capability, regardless of drawer vs dialog or expanded vs collapsed.
    const { loopCapability, isShuffleSupported } = useGuideQueue();
    const showLoopControls = loopCapability !== LoopCapability.Unsupported;
    // Shuffle renders only for eligible content, independent of the loop
    // capability: single tracks can loop but never shuffle. EVE-11931.
    // Soundscapes are never shuffle-supported, so folding `!isSoundScape` in
    // is belt-and-braces.
    const showShuffleControls = isShuffleSupported && !isSoundScape;

    const sideButtonSize: 'xsmall' | 'playerControl' = isCollapsed
        ? 'xsmall'
        : 'playerControl';
    const playPauseSize: 'medium' | 'largePlayerControl' = isExpanded
        ? 'largePlayerControl'
        : 'medium';

    const isDisplayingSubtitles =
        !isCollapsed && isShowingSubtitles && !isSoundScape;

    return (
        <>
            {isDisplayingSubtitles && (
                <Box sx={styleCenteredSubtitlesWrapper}>
                    <AudioControlsSubtitles
                        fadeIn={areControlsHidden}
                        isMuted={isMuted}
                        sx={isFloating ? styleFloatingSubtitles : undefined}
                    />
                </Box>
            )}
            <Slide
                direction="up"
                in={!areControlsHidden}
                mountOnEnter
                unmountOnExit
            >
                <Box sx={sx}>
                    {isDisplayingSubtitles && (
                        <AudioControlsSubtitles
                            isMuted={isMuted}
                            sx={sxAppend(
                                styleSubtitles(isShowingSubtitles),
                                isFloating && styleFloatingSubtitles
                            )}
                        />
                    )}
                    <Box
                        sx={sxAppend(
                            styleAudioControlsContainer,
                            isFloating && styleFloatingContainer,
                            controlsSx
                        )}
                    >
                        <Box sx={styleControlsMain}>
                            <Box
                                sx={sxAppend(
                                    styleNonMobileElement,
                                    styleMetadataContainer
                                )}
                            >
                                <Box sx={styleMetadata}>
                                    {image}
                                    {!isMobile && (title || subtitle) && (
                                        <Box sx={styleTitleSubtitle}>
                                            <Box sx={styleTitleContainer}>
                                                {drawerTitleIcon ? (
                                                    <Box
                                                        sx={
                                                            styleDrawerTitleIcon
                                                        }
                                                    >
                                                        {drawerTitleIcon}
                                                    </Box>
                                                ) : null}
                                                <Typography
                                                    variant="h6"
                                                    component="p"
                                                    color="text.body"
                                                    sx={styleMaxLines(2)}
                                                >
                                                    {title}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="subtitle2"
                                                component="p"
                                                color="text.sub"
                                                sx={styleMaxLines(1)}
                                            >
                                                {subtitle}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={styleNonMobileElement}></Box>
                            <Box sx={styleControlsCenter}>
                                <Box
                                    sx={sxAppend(
                                        styleControlsButtonsTimeline,
                                        isExpanded &&
                                            styleControlsButtonsNoTimeline
                                    )}
                                >
                                    <Box
                                        sx={sxAppend(
                                            styleControlsButtons,
                                            isCollapsed &&
                                                styleControlsButtonsCollapsed
                                        )}
                                    >
                                        {showShuffleControls ? (
                                            <ShuffleButton
                                                size={sideButtonSize}
                                            />
                                        ) : (
                                            // Balance the transport row when a
                                            // loop control is shown but shuffle
                                            // isn't (single loopable track): show
                                            // a dimmed, disabled shuffle glyph so
                                            // the row stays centered (EVE-13089).
                                            showLoopControls && (
                                                <ShuffleButton
                                                    size={sideButtonSize}
                                                    disabled
                                                />
                                            )
                                        )}
                                        {!isSoundScape && (
                                            <PreviousButton
                                                isPlaylist={isPlaylist}
                                                onPreviousTrack={
                                                    onPreviousTrack
                                                }
                                                size={sideButtonSize}
                                            />
                                        )}
                                        <PlayPauseButton
                                            size={playPauseSize}
                                            onControlClick={onControlClick}
                                        />
                                        {!isSoundScape && (
                                            <NextButton
                                                isPlaylist={isPlaylist}
                                                onNextTrack={onNextTrack}
                                                size={sideButtonSize}
                                            />
                                        )}
                                        {showLoopControls && (
                                            <LoopButton size={sideButtonSize} />
                                        )}
                                    </Box>
                                    {isCollapsed && !isSoundScape && (
                                        <AudioTimeline
                                            variant={AudioTimelineVariant.Small}
                                        />
                                    )}
                                </Box>
                            </Box>
                            <Box
                                sx={sxAppend(
                                    styleNonMobileElement,
                                    styleCustomActionContainer
                                )}
                            >
                                {!isExpanded && customAction}
                            </Box>
                        </Box>
                    </Box>
                    {isExpanded && !isSoundScape && <AudioTimeline />}
                    {isExpanded && (
                        <Box sx={styleBottomActionsRow}>
                            {!isSoundScape && (
                                <CcButton size="medium" variant="contained" />
                            )}
                            {customAction}
                            {supportsPlaybackSpeed && (
                                <PlaybackSpeedButton
                                    // playbackRate is always a number in production
                                    // (useMediaControls defaults it to 1); the fallback
                                    // guards test mocks that cast a partial MediaControls.
                                    playbackRate={
                                        playbackRate ?? NORMAL_PLAYBACK_RATE
                                    }
                                    onChange={setPlaybackRate}
                                    onOpenChange={onPlaybackMenuOpenChange}
                                />
                            )}
                            {supportsAddToPlaylist && onAddToPlaylist && (
                                <AddToPlaylistButton
                                    onClick={onAddToPlaylist}
                                />
                            )}
                            <MuteButton size="medium" variant="contained" />
                        </Box>
                    )}
                </Box>
            </Slide>
        </>
    );
}
